import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { ArrowRight, Sparkles, Cpu, Code, Monitor, Zap, Binary, Wifi, CircuitBoard, Braces, Globe, Bot } from 'lucide-react';

const Hero = () => {
  const { t } = useLanguage();
  const [currentTech, setCurrentTech] = useState(0);

  const techWords = t.nav.courses === 'Kurslar' 
    ? ['Arduino', 'Python', 'HTML', 'CSS', 'JavaScript', 'Robotlar']
    : ['Arduino', 'Python', 'HTML', 'CSS', 'JavaScript', 'Robots'];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTech((prev) => (prev + 1) % techWords.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [techWords.length]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-sky-50 to-cyan-50"></div>
      
      {/* Animated Background Particles */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-sky-200/30 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-200/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-100/20 rounded-full blur-3xl"></div>
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#e0e7ff15_1px,transparent_1px),linear-gradient(to_bottom,#e0e7ff15_1px,transparent_1px)] bg-[size:3rem_3rem]"></div>

      {/* Floating Tech Icons - Left Side */}
      <div className="absolute left-4 sm:left-8 lg:left-16 top-1/2 -translate-y-1/2 hidden md:flex flex-col space-y-6">
        <div className="animate-float">
          <div className="w-14 h-14 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg flex items-center justify-center hover:scale-110 transition-transform cursor-pointer group">
            <Cpu className="w-7 h-7 text-sky-500 group-hover:text-cyan-500 transition-colors" />
          </div>
        </div>
        <div className="animate-float delay-300">
          <div className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg flex items-center justify-center hover:scale-110 transition-transform cursor-pointer group">
            <Binary className="w-6 h-6 text-cyan-500 group-hover:text-sky-500 transition-colors" />
          </div>
        </div>
        <div className="animate-float delay-700">
          <div className="w-14 h-14 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg flex items-center justify-center hover:scale-110 transition-transform cursor-pointer group">
            <CircuitBoard className="w-7 h-7 text-blue-500 group-hover:text-cyan-500 transition-colors" />
          </div>
        </div>
      </div>

      {/* Floating Tech Icons - Right Side */}
      <div className="absolute right-4 sm:right-8 lg:right-16 top-1/2 -translate-y-1/2 hidden md:flex flex-col space-y-6">
        <div className="animate-float delay-500">
          <div className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg flex items-center justify-center hover:scale-110 transition-transform cursor-pointer group">
            <Code className="w-6 h-6 text-cyan-500 group-hover:text-sky-500 transition-colors" />
          </div>
        </div>
        <div className="animate-float delay-200">
          <div className="w-14 h-14 bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg flex items-center justify-center hover:scale-110 transition-transform cursor-pointer group">
            <Braces className="w-7 h-7 text-sky-500 group-hover:text-cyan-500 transition-colors" />
          </div>
        </div>
        <div className="animate-float delay-1000">
          <div className="w-12 h-12 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg flex items-center justify-center hover:scale-110 transition-transform cursor-pointer group">
            <Wifi className="w-6 h-6 text-blue-500 group-hover:text-sky-500 transition-colors" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="text-center max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-md mb-8 hover:shadow-lg transition-shadow">
            <Sparkles className="w-4 h-4 text-sky-500" />
            <span className="text-sm font-medium text-slate-600">İnfoLab Tədris Mərkəzi</span>
            <Zap className="w-4 h-4 text-cyan-500" />
          </div>

          {/* Main Title */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-800 leading-tight mb-6">
            {t.hero.title}
            <span className="block mt-2 bg-gradient-to-r from-sky-500 via-cyan-500 to-blue-500 bg-clip-text text-transparent">
              {t.hero.titleHighlight}
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto mb-8 leading-relaxed">
            {t.hero.subtitle}
          </p>

          {/* Interactive Tech Showcase */}
          <div className="mb-10">
            <div className="inline-flex items-center justify-center">
              <div className="relative">
                {/* Main Container */}
                <div className="flex items-center gap-2 bg-white/80 backdrop-blur-xl px-2 py-2 rounded-2xl shadow-xl border border-slate-200/50">
                  
                  {/* Animated Icon */}
                  <div className="relative">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-500 ${
                      currentTech === 0 ? 'bg-gradient-to-br from-teal-400 to-cyan-500' :
                      currentTech === 1 ? 'bg-gradient-to-br from-green-400 to-emerald-500' :
                      currentTech === 2 ? 'bg-gradient-to-br from-orange-400 to-rose-500' :
                      currentTech === 3 ? 'bg-gradient-to-br from-blue-400 to-indigo-500' :
                      currentTech === 4 ? 'bg-gradient-to-br from-yellow-400 to-amber-500' :
                      'bg-gradient-to-br from-sky-400 to-blue-600'
                    }`}>
                      {currentTech === 0 && <Cpu className="w-6 h-6 text-white" />}
                      {currentTech === 1 && <Code className="w-6 h-6 text-white" />}
                      {currentTech === 2 && <Globe className="w-6 h-6 text-white" />}
                      {currentTech === 3 && <Braces className="w-6 h-6 text-white" />}
                      {currentTech === 4 && <Monitor className="w-6 h-6 text-white" />}
                      {currentTech === 5 && <Bot className="w-6 h-6 text-white" />}
                    </div>
                    {/* Pulse ring */}
                    <div className={`absolute inset-0 rounded-xl animate-ping opacity-30 ${
                      currentTech === 0 ? 'bg-cyan-400' :
                      currentTech === 1 ? 'bg-emerald-400' :
                      currentTech === 2 ? 'bg-rose-400' :
                      currentTech === 3 ? 'bg-indigo-400' :
                      currentTech === 4 ? 'bg-amber-400' :
                      'bg-blue-400'
                    }`}></div>
                  </div>
          
                  {/* Text Content */}
                  <div className="pr-4">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 font-medium">
                      {t.nav.courses === 'Kurslar' ? 'İndi öyrən' : 'Learn now'}
                    </div>
                    <div className="text-xl font-bold text-slate-800 min-w-[100px]">
                      {techWords[currentTech]}
                    </div>
                  </div>
          
                  {/* Progress Dots */}
                  <div className="flex flex-col gap-1 px-2 border-l border-slate-200">
                    {techWords.map((_, index) => (
                      <div
                        key={index}
                        className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                          index === currentTech 
                            ? 'bg-sky-500 scale-125' 
                            : 'bg-slate-300'
                        }`}
                      />
                    ))}
                  </div>
                </div>
          
                {/* Decorative Elements */}
                <div className="absolute -top-2 -right-2 w-4 h-4 bg-cyan-400 rounded-full animate-bounce opacity-60"></div>
                <div className="absolute -bottom-1 -left-1 w-3 h-3 bg-sky-400 rounded-full animate-pulse opacity-60"></div>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => scrollToSection('courses')}
              className="group bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white px-8 py-6 text-lg rounded-xl shadow-lg shadow-sky-500/25 transition-all hover:shadow-xl hover:shadow-sky-500/30 hover:-translate-y-1"
            >
              {t.hero.cta}
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              onClick={() => scrollToSection('contact')}
              variant="outline"
              className="border-2 border-slate-200 hover:border-sky-300 hover:bg-sky-50 px-8 py-6 text-lg rounded-xl transition-all hover:-translate-y-1"
            >
              {t.hero.ctaSecondary}
            </Button>
          </div>

          {/* Tech Stack - Animated */}
          <div className="mt-16">
            <p className="text-sm text-slate-400 mb-6">
              {t.nav.courses === 'Kurslar' ? 'İstifadə etdiyimiz texnologiyalar' : 'Technologies we use'}
            </p>
            
            {/* Animated Tech Cards */}
            <div className="flex items-center justify-center flex-wrap gap-3">
              {[
                { name: 'Arduino', color: 'from-teal-400 to-cyan-500', icon: Cpu },
                { name: 'Python', color: 'from-green-400 to-emerald-500', icon: Code },
                { name: 'HTML/CSS', color: 'from-orange-400 to-rose-500', icon: Globe },
                { name: 'JavaScript', color: 'from-yellow-400 to-amber-500', icon: Braces },
                { name: 'Robotics', color: 'from-sky-400 to-blue-600', icon: Bot },
              ].map((tech, index) => (
                <div
                  key={index}
                  className="group relative"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {/* Card */}
                  <div className={`relative bg-gradient-to-br ${tech.color} p-4 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105 cursor-pointer overflow-hidden`}>
                    {/* Glow effect */}
                    <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    
                    {/* Icon */}
                    <tech.icon className="w-8 h-8 text-white relative z-10" />
                    
                    {/* Shine effect */}
                    <div className="absolute top-0 -left-full w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:left-full transition-all duration-700 ease-in-out"></div>
                  </div>
                  
                  {/* Label */}
                  <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 group-hover:-bottom-8 transition-all duration-300">
                    <span className="text-xs font-semibold text-slate-600 whitespace-nowrap bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full shadow-md">
                      {tech.name}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Decorative line */}
            <div className="mt-12 flex items-center justify-center">
              <div className="h-px w-16 bg-gradient-to-r from-transparent via-sky-300 to-transparent"></div>
              <Sparkles className="w-4 h-4 text-sky-400 mx-3" />
              <div className="h-px w-16 bg-gradient-to-r from-transparent via-sky-300 to-transparent"></div>
            </div>
          </div>

        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="white"/>
        </svg>
      </div>
    </section>
  );
};

export default Hero;
